import { apiClient } from './client';
import type { ChainStep } from './sops.api';

/**
 * Client for `/api/investigations/*` (ADR-049). Stays in lockstep with
 * `server/src/modules/investigations/{config,taxonomy}.ts`.
 */

export type EscalationStrategy = 'least_loaded' | 'round_robin';
export type CsTicketCandidateMode = 'off' | 'tags_only' | 'ai_classify';

export interface InvestigationConfig {
  enabled: boolean;
  requireQualityReview: boolean;
  requireSignedClosure: boolean;
  requireReasonForChange: boolean;
  requireStationAttribution: boolean;
  requireCorrectiveAction: boolean;
  requireInconclusiveReason: boolean;
  requireIndependentReviewer: boolean;
  allowPersonAttribution: boolean;
  investigatorPoolMembershipIds: string[];
  escalationStrategy: EscalationStrategy;
  qualityReviewer: ChainStep;
  unassignedNotify: ChainStep;
  csTicketCandidateMode: CsTicketCandidateMode;
  candidateTriggerTags: string[];
  candidateKeywords: string[];
  candidateMinConfidence: number;
  candidateMaxAiCallsPerRun: number;
  candidateAlertTypes: string[];
  zendeskClassifyEnabled: boolean;
  resolutionActionOptions: string[];
}

export type InvestigationConfigPatch = Partial<InvestigationConfig>;

export const TAXONOMY_KINDS = ['issue_types', 'causes', 'stations', 'inconclusive_reasons'] as const;
export type TaxonomyKind = (typeof TAXONOMY_KINDS)[number];

export interface TaxonomyItem {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
  active: boolean;
  category: string | null;
  departmentId: string | null;
}

export interface TaxonomyBundle {
  issueTypes: TaxonomyItem[];
  causes: TaxonomyItem[];
  stations: TaxonomyItem[];
  inconclusiveReasons: TaxonomyItem[];
}

export interface CreateTaxonomyItemRequest {
  name: string;
  slug?: string;
  category?: string;
  departmentId?: string | null;
  sortOrder?: number;
}

export interface UpdateTaxonomyItemRequest {
  name?: string;
  category?: string;
  departmentId?: string | null;
  sortOrder?: number;
  active?: boolean;
}

const BASE = '/api/investigations';

export async function getInvestigationConfig(): Promise<InvestigationConfig> {
  const res = await apiClient.get<{ data: InvestigationConfig }>(`${BASE}/config`);
  return res.data.data;
}

/** Needs `manage_investigations`. Sends only the fields that changed. */
export async function updateInvestigationConfig(
  patch: InvestigationConfigPatch,
): Promise<InvestigationConfig> {
  const res = await apiClient.put<{ data: InvestigationConfig }>(`${BASE}/config`, patch);
  return res.data.data;
}

export async function listInvestigationTaxonomy(
  opts: { includeInactive?: boolean } = {},
): Promise<TaxonomyBundle> {
  const res = await apiClient.get<{ data: TaxonomyBundle }>(`${BASE}/taxonomy`, {
    params: opts.includeInactive ? { includeInactive: '1' } : {},
  });
  return res.data.data;
}

export async function createTaxonomyItem(
  kind: TaxonomyKind,
  body: CreateTaxonomyItemRequest,
): Promise<TaxonomyItem> {
  const res = await apiClient.post<{ data: TaxonomyItem }>(`${BASE}/taxonomy/${kind}`, body);
  return res.data.data;
}

export async function updateTaxonomyItem(
  kind: TaxonomyKind,
  id: string,
  body: UpdateTaxonomyItemRequest,
): Promise<TaxonomyItem> {
  const res = await apiClient.patch<{ data: TaxonomyItem }>(
    `${BASE}/taxonomy/${kind}/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

/** Adds the generic starter vocabulary; idempotent by slug. */
export async function addStarterTaxonomy(): Promise<{ created: number }> {
  const res = await apiClient.post<{ data: { created: number } }>(`${BASE}/taxonomy/starter`);
  return res.data.data;
}
